/* eslint-disable @typescript-eslint/no-use-before-define */

import moment from 'moment';
import isNil from 'lodash/isNil';

import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {PropertyFilterDateRange} from 'common/widgets/date-range-picker/models/property-filter-date-range';
import {PropertyType, ResourceType, STRING_TYPECAST_PROPERTIES} from 'common/report/constants';
import {findKeyByValue} from 'common/util/typescript';
import {CustomProperty, Segmentation} from 'common/types/reports/query';

import {PropertyFilter} from './property-filter';
import {
  BooleanOperator,
  FilterOperand,
  FilterOperator,
  FilterValue,
  ListOperator,
  ListQuantifier,
  NumberOperator,
  StringOperator,
} from './types';

export const RESOURCE_TYPE_MAP: {[key in ResourceType]?: string} = {
  [ResourceType.Event]: `properties`,
  [ResourceType.User]: `user`,
  [ResourceType.Cohort]: `cohort`,
  [ResourceType.Other]: `other`,
};

const DATETIME_OPERATOR_MAP: {[key in DateRangeType]?: string} = {
  [DateRangeType.Between]: `><`,
  [DateRangeType.NotBetween]: `!><`,
  [DateRangeType.On]: `==`,
  [DateRangeType.NotOn]: `!=`,
  [DateRangeType.RelativeBefore]: `>`,
  [DateRangeType.NotRelativeAfter]: `>`,
  [DateRangeType.RelativeAfter]: `<`,
  [DateRangeType.Since]: `<`,
  [DateRangeType.Before]: `>`,
};

const LIST_OPERATOR_MAP: {[key in ListOperator]?: string} = {
  [ListOperator.Contains]: `in`,
  [ListOperator.DoesNotContain]: `not in`,
};

const NUMBER_OPERATOR_MAP: {[key in NumberOperator]?: string} = {
  [NumberOperator.Between]: `><`,
  [NumberOperator.NotBetween]: `!><`,
  [NumberOperator.AtLeast]: `>=`,
  [NumberOperator.AtMost]: `<=`,
  [NumberOperator.EqualTo]: `==`,
  [NumberOperator.NotEqualTo]: `!=`,
  [NumberOperator.GreaterThan]: `>`,
  [NumberOperator.LessThan]: `<`,
  [NumberOperator.Set]: `is set`,
  [NumberOperator.NotSet]: `is not set`,
};

const STRING_OPERATOR_MAP: {[key in StringOperator]: string} = {
  [StringOperator.Contains]: `in`,
  [StringOperator.DoesNotContain]: `not in`,
  [StringOperator.DoesNotEqual]: `!=`,
  [StringOperator.Equals]: `==`,
  [StringOperator.NotSet]: `not set`,
  [StringOperator.Set]: `set`,
};

const TIME_UNIT_MAP: {[key in TimeUnit]?: string} = {
  [TimeUnit.Hour]: `hours`,
  [TimeUnit.Day]: `days`,
  [TimeUnit.Week]: `weeks`,
  [TimeUnit.Month]: `months`,
};

export interface SegfilterFilter {
  operand: FilterValue;
  operator?: string;
  segments?: any[];
  unit?: string;
  listQuantifier?: ListQuantifier;
}

export interface Property {
  customProperty?: CustomProperty;
  customPropertyId?: string | number;
  data_group_id?: string;
  joinPropertyType?: PropertyType;
  name: string | string[];
  source: string;
  tempDataGroupId?: string;
  type?: PropertyType;
  scdEvent?: string;
}

/**
 * A SegFilter is a legacy way of defining filters and segmentation in the UI, it came from
 * what was defined in the Backbone.js filtering widgets, this format is currently used in:
 *
 * - Funnels, Flows & Retention inline filters (per event)
 * - Custom events inline filters
 */
export interface Segfilter {
  property: Property;
  type: PropertyType;
  selected_property_type: PropertyType;
  filter: SegfilterFilter;
}

export const SegFilterProps: Array<keyof Segfilter> = [`property`, `type`, `selected_property_type`, `filter`];

// TODO: Move this conversion file out of this component.
export interface SegfilterSegmentation {
  property: Property;
  type: PropertyType;
  selected_property_type: PropertyType;
}

/**
 * @deprecated obsolete filtering related UI utils
 */
export class CustomEventPropertyFilter extends PropertyFilter {
  public static fromSegfilter(segfilter: Segfilter): CustomEventPropertyFilter {
    return convertSegfilterToPropertyFilter(segfilter);
  }

  public createDateRange(attributes: Partial<DateRange> = {}): DateRange {
    return new PropertyFilterDateRange(attributes);
  }

  public toSegfilter(): Segfilter {
    return convertPropertyFilterToSegfilter(this);
  }
}

/**
 * @deprecated obsolete filtering related UI utils
 */
export function convertSegfilterSegmentationToPropertySegmentation(
  segfilterSegmentation: SegfilterSegmentation,
): Segmentation {
  return {
    propertyName: segfilterSegmentation.property.name,
    resourceType: findKeyByValue(RESOURCE_TYPE_MAP, segfilterSegmentation.property.source),
    ...getPropertyTypesFromSegfilter(segfilterSegmentation),
  };
}

/**
 * For a lack of a better standard term, `Segmentation` here means modern UI provided filters
 */
export function isSegmentationFilter(filter: Segfilter | Segmentation): filter is Segmentation {
  return filter && Object.prototype.hasOwnProperty.call(filter, `selected_property_type`) === false;
}

/**
 * @deprecated obsolete filtering related UI utils
 */
export function convertSegfilterToPropertyFilter(segfilter: Segfilter | Segmentation): CustomEventPropertyFilter {
  if (isSegmentationFilter(segfilter)) {
    return new CustomEventPropertyFilter({...segfilter});
  }

  const {
    name,
    source,
    data_group_id: dataGroupId,
    tempDataGroupId,
    joinPropertyType,
    customProperty,
    customPropertyId,
    scdEvent,
  } = segfilter.property;
  const {listQuantifier} = segfilter.filter;
  const propertyType = STRING_TYPECAST_PROPERTIES.includes(Array.isArray(name) ? name[0] : name)
    ? PropertyType.String
    : segfilter.type;

  return new CustomEventPropertyFilter({
    propertyName: name,
    resourceType: findKeyByValue(RESOURCE_TYPE_MAP, source),
    ...getPropertyTypesFromSegfilter(segfilter),
    ...toPropertyFilterFilter(segfilter, propertyType),
    ...(dataGroupId && !joinPropertyType ? {resourceType: ResourceType.User} : {}),
    ...(dataGroupId ? {dataGroupId} : {}),
    ...(tempDataGroupId ? {tempDataGroupId} : {}),
    ...(joinPropertyType ? {joinPropertyType} : {}),
    ...(scdEvent ? {scdEvent} : {}),
    ...(customProperty ? {customProperty} : {}),
    /** @todo: [shawn] Remove any here once we land customPropertyId type changes from View Users PR */
    ...(customPropertyId ? {customPropertyId: customPropertyId as any} : {}),
    ...(listQuantifier ? {listQuantifier} : {}),
  });
}

// TODO do we still need this? not used anywhere
/**
 * @deprecated obsolete filtering related UI utils
 */
export function convertPropertySegmentationToSegfilterSegmentation(segmentation: Segmentation): SegfilterSegmentation {
  return {
    property: {
      name: segmentation.propertyName,
      source: RESOURCE_TYPE_MAP[segmentation.resourceType],
      type: segmentation.propertyDefaultType,
      ...(segmentation.joinPropertyType ? {joinPropertyType: segmentation.joinPropertyType} : {}),
      ...(segmentation.scdEvent ? {scdEvent: segmentation.scdEvent} : {}),
    },
    selected_property_type: segmentation.propertyType,
    type: segmentation.propertyDefaultType,
  };
}

/**
 * @deprecated obsolete filtering related UI utils
 */
export function convertPropertyFilterToSegfilter(propertyFilter: PropertyFilter): Segfilter {
  /* eslint-disable camelcase */
  return {
    filter: toSegfilterFilter(propertyFilter),
    property: {
      name: propertyFilter.propertyName,
      source: RESOURCE_TYPE_MAP[propertyFilter.resourceType],
      type: propertyFilter.propertyDefaultType,
      ...(propertyFilter.joinPropertyType ? {joinPropertyType: propertyFilter.joinPropertyType} : {}),
      ...(propertyFilter.dataGroupId ? {data_group_id: propertyFilter.dataGroupId} : {}),
      ...(propertyFilter.tempDataGroupId ? {data_group_id: propertyFilter.tempDataGroupId} : {}),
      ...(propertyFilter.customProperty ? {customProperty: propertyFilter.customProperty} : {}),
      ...(propertyFilter.customPropertyId ? {customPropertyId: propertyFilter.customPropertyId} : {}),
      ...(propertyFilter.scdEvent ? {scdEvent: propertyFilter.scdEvent} : {}),
    },
    selected_property_type: propertyFilter.propertyDefaultType,
    type: propertyFilter.propertyType,
  };
  /* eslint-enable camelcase */
}

/**
 * @deprecated obsolete filtering related UI utils
 */
function getPropertyTypesFromSegfilter(segfilter: Segfilter | SegfilterSegmentation) {
  let propertyType = segfilter.type;
  const joinPropertyType = segfilter.property.joinPropertyType;
  let propertyDefaultType = segfilter.selected_property_type;

  // handle reserved ID properties and apply a string typecast
  if (
    STRING_TYPECAST_PROPERTIES.includes(
      Array.isArray(segfilter.property.name) ? segfilter.property.name[0] : segfilter.property.name,
    )
  ) {
    propertyDefaultType = PropertyType.Number;
    propertyType = PropertyType.String;
  }

  return {
    propertyDefaultType,
    propertyType,
    ...(joinPropertyType ? {joinPropertyType} : {}),
  };
}

/**
 * @deprecated obsolete filtering related UI utils
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function toPropertyFilterFilter(
  segfilter: Segfilter,
  type: PropertyType,
): {filterOperator: FilterOperator; filterValue: FilterValue; propertyObjectKey?: string} {
  const {filter} = segfilter;
  let filterOperator;
  let filterValue = null;
  let propertyObjectKey = null;

  switch (type) {
    case `object`: {
      const filterSegments = filter.segments || [];
      propertyObjectKey = filterSegments.length === 2 ? filterSegments[1].toString() : null;
      filterValue = isNil(filter.operand) ? null : [filter.operand];
      filterOperator = filterValue ? findKeyByValue(STRING_OPERATOR_MAP, filter.operator) : StringOperator.Set;
      break;
    }
    case `boolean`: {
      switch (filter.operand) {
        case `false`:
          filterOperator = BooleanOperator.False;
          filterValue = false;
          break;
        case `true`:
          filterOperator = BooleanOperator.True;
          filterValue = true;
          break;
        default:
          break;
      }
      break;
    }
    case `datetime`: {
      filterOperator = findKeyByValue(DATETIME_OPERATOR_MAP, filter.operator);
      switch (filterOperator) {
        case DateRangeType.RelativeAfter:
        case DateRangeType.RelativeBefore:
        case DateRangeType.Since:
        case DateRangeType.Before: {
          if (filter.unit) {
            filterValue = new DateRange({
              type: filterOperator,
              window: {
                unit: findKeyByValue(TIME_UNIT_MAP, filter.unit),
                value: filter.operand as number,
              },
            });
          } else {
            // temporary fix for handling date range "Since" & "Before" because they use same operand as "RelativeBefore" & "RelativeAfter"
            const propertyFilter = segfilterDateToPropertyFilter(filter.operand as string);
            const newFilterOperatorType =
              filterOperator === DateRangeType.RelativeAfter ? DateRangeType.Since : DateRangeType.Before;
            filterValue = new DateRange({
              ...(filterOperator === DateRangeType.RelativeAfter ? {from: propertyFilter} : {to: propertyFilter}),
              type: newFilterOperatorType,
            });
            filterOperator = newFilterOperatorType;
          }
          break;
        }
        case DateRangeType.On:
        case DateRangeType.NotOn:
        case DateRangeType.Between:
        case DateRangeType.NotBetween: {
          let operand1 = null;
          let operand2 = null;
          if ([DateRangeType.On, DateRangeType.NotOn].includes(filterOperator)) {
            const operand = filter.operand as string;
            [operand1, operand2] = [operand, operand];
          } else {
            [operand1, operand2] = filter.operand as string[];
          }
          filterValue = new DateRange({
            from: segfilterDateToPropertyFilter(operand1),
            to: segfilterDateToPropertyFilter(operand2),
            type: filterOperator,
          });
          break;
        }
        default:
          break;
      }
      break;
    }
    case `list`: {
      filterOperator = findKeyByValue(LIST_OPERATOR_MAP, filter.operator);
      filterValue = filter.operand;
      break;
    }
    case `number`: {
      filterOperator = findKeyByValue(NUMBER_OPERATOR_MAP, filter.operator);
      switch (filterOperator as NumberOperator) {
        case NumberOperator.Between:
        case NumberOperator.NotBetween: {
          const operand = filter.operand as string[];
          filterValue = [Number(operand[0]), Number(operand[1])];
          break;
        }
        case NumberOperator.EqualTo:
        case NumberOperator.NotEqualTo:
        case NumberOperator.AtLeast:
        case NumberOperator.AtMost:
        case NumberOperator.GreaterThan:
        case NumberOperator.LessThan: {
          filterValue = Number(filter.operand);
          break;
        }
        default:
          break;
      }
      break;
    }
    case `string`: {
      filterOperator = findKeyByValue(STRING_OPERATOR_MAP, filter.operator);

      switch (filterOperator as StringOperator) {
        case StringOperator.Equals:
        case StringOperator.DoesNotEqual: {
          filterValue = Array.isArray(filter.operand) ? (filter.operand as string[]) : [filter.operand];
          break;
        }
        case StringOperator.Contains:
        case StringOperator.DoesNotContain: {
          filterValue = filter.operand as string;
          break;
        }
        default:
          break;
      }
      break;
    }
    case `other`: {
      filterOperator = filter.operator;
      break;
    }
    default:
      break;
  }

  if (type !== `object` && filter.segments && filter.segments.length > 1) {
    propertyObjectKey = filter.segments.slice(1).map((value) => value.toString());
  }

  return {
    filterOperator,
    filterValue,
    ...(propertyObjectKey ? {propertyObjectKey} : null),
  };
}

/**
 * @deprecated obsolete filtering related UI utils
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function toSegfilterFilter(propertyFilter: PropertyFilter): SegfilterFilter {
  const {filterOperator, propertyType, propertyName, listQuantifier} = propertyFilter;
  let {filterValue} = propertyFilter;

  let propertyObjectKey;
  if (Array.isArray(propertyFilter.propertyObjectKey)) {
    propertyObjectKey = propertyFilter.propertyObjectKey;
  } else if (typeof propertyFilter.propertyObjectKey === `string`) {
    propertyObjectKey = [propertyObjectKey];
  }
  const segments = propertyObjectKey ? [propertyName, ...propertyObjectKey] : null;

  switch (propertyType) {
    case PropertyType.Object: {
      const operator = STRING_OPERATOR_MAP[filterOperator as StringOperator];

      return {
        operand: Array.isArray(filterValue) ? filterValue[0] : null,
        operator,
        segments,
        ...(listQuantifier ? {listQuantifier} : {}),
      };
    }
    case PropertyType.Boolean: {
      let operand = null;
      switch (filterOperator as BooleanOperator) {
        case BooleanOperator.False: {
          operand = `false`;
          break;
        }
        case BooleanOperator.True: {
          operand = `true`;
          break;
        }
        default:
          break;
      }
      return {operand, ...(segments ? {segments} : {}), ...(listQuantifier ? {listQuantifier} : {})};
    }
    case PropertyType.Datetime: {
      const dateRange = filterValue as DateRange;

      if (dateRange.rolling) {
        return null;
      }

      const operator = DATETIME_OPERATOR_MAP[filterOperator as DateRangeType];
      let operand;
      let unit;
      switch (filterOperator as DateRangeType) {
        case DateRangeType.Before: {
          operand = propertyFilterDateToSegfilter(dateRange.to as string);
          break;
        }
        case DateRangeType.Since: {
          operand = propertyFilterDateToSegfilter(dateRange.from as string);
          break;
        }
        case DateRangeType.NotRelativeAfter:
        case DateRangeType.RelativeAfter:
        case DateRangeType.RelativeBefore: {
          operand = dateRange.window.value;
          unit = TIME_UNIT_MAP[dateRange.window.unit];
          break;
        }
        case DateRangeType.On:
        case DateRangeType.NotOn:
          operand = propertyFilterDateToSegfilter(dateRange.from as string);
          break;
        case DateRangeType.Between:
        case DateRangeType.NotBetween: {
          operand = [
            propertyFilterDateToSegfilter(dateRange.from as string),
            propertyFilterDateToSegfilter(dateRange.to as string),
          ];
          break;
        }
        default:
          break;
      }
      return {
        operator,
        operand,
        ...(unit ? {unit} : {}),
        ...(segments ? {segments} : {}),
        ...(listQuantifier ? {listQuantifier} : {}),
      };
    }
    case PropertyType.List: {
      return {
        operand: filterValue as string,
        operator: LIST_OPERATOR_MAP[filterOperator as ListOperator],
        ...(segments ? {segments} : {}),
        ...(listQuantifier ? {listQuantifier} : {}),
      };
    }
    case PropertyType.Number: {
      const operator = NUMBER_OPERATOR_MAP[filterOperator as NumberOperator];
      let operand;
      switch (filterOperator as NumberOperator) {
        case NumberOperator.NotSet:
        case NumberOperator.Set: {
          operand = ``;
          break;
        }
        case NumberOperator.Between:
        case NumberOperator.NotBetween: {
          filterValue = filterValue as FilterOperand[];
          operand = [filterValue[0].toString(), filterValue[1].toString()];
          break;
        }
        case NumberOperator.EqualTo:
        case NumberOperator.NotEqualTo:
        case NumberOperator.AtLeast:
        case NumberOperator.AtMost:
        case NumberOperator.GreaterThan:
        case NumberOperator.LessThan: {
          operand = filterValue.toString();
          break;
        }
        default:
          break;
      }
      return {operator, operand, ...(segments ? {segments} : {}), ...(listQuantifier ? {listQuantifier} : {})};
    }
    case PropertyType.String: {
      const operator = STRING_OPERATOR_MAP[filterOperator as StringOperator];
      let operand;
      switch (filterOperator as StringOperator) {
        case StringOperator.Equals:
        case StringOperator.DoesNotEqual: {
          operand = filterValue as string[];
          break;
        }
        case StringOperator.Contains:
        case StringOperator.DoesNotContain: {
          operand = filterValue as string;
          break;
        }
        case StringOperator.NotSet:
        case StringOperator.Set: {
          operand = ``;
          break;
        }
        default:
          break;
      }
      return {operator, operand, ...(segments ? {segments} : {}), ...(listQuantifier ? {listQuantifier} : {})};
    }
    case PropertyType.NonProperty: {
      return {
        operator: filterOperator,
        operand: null,
        ...(segments ? {segments} : {}),
        ...(listQuantifier ? {listQuantifier} : {}),
      };
    }
    default:
      return null;
  }
}

const SEGFILTER_DATE_FORMAT = `MM/DD/YYYY`;
const PROPERTY_FILTER_DATE_FORMAT = `YYYY-MM-DD`;

/**
 * @deprecated obsolete filtering related UI utils
 */
function propertyFilterDateToSegfilter(dateOperand: string): string {
  return moment.utc(dateOperand, PROPERTY_FILTER_DATE_FORMAT).format(SEGFILTER_DATE_FORMAT);
}

/**
 * @deprecated obsolete filtering related UI utils
 */
function segfilterDateToPropertyFilter(dateOperand: string): string {
  return moment.utc(dateOperand, SEGFILTER_DATE_FORMAT).format(PROPERTY_FILTER_DATE_FORMAT);
}
